/** 2D vector math. Units are millimetres unless stated otherwise; y axis points down. */

export interface Vec {
  x: number;
  y: number;
}

/** Geometric tolerance used for comparisons in millimetres (1 µm). */
export const EPS = 1e-6;

export const vec = (x: number, y: number): Vec => ({ x, y });
export const add = (a: Vec, b: Vec): Vec => ({ x: a.x + b.x, y: a.y + b.y });
export const sub = (a: Vec, b: Vec): Vec => ({ x: a.x - b.x, y: a.y - b.y });
export const scale = (a: Vec, s: number): Vec => ({ x: a.x * s, y: a.y * s });
export const dot = (a: Vec, b: Vec): number => a.x * b.x + a.y * b.y;
export const cross = (a: Vec, b: Vec): number => a.x * b.y - a.y * b.x;
export const len = (a: Vec): number => Math.hypot(a.x, a.y);
export const dist = (a: Vec, b: Vec): number => Math.hypot(a.x - b.x, a.y - b.y);
export const dist2 = (a: Vec, b: Vec): number => (a.x - b.x) ** 2 + (a.y - b.y) ** 2;
export const lerp = (a: Vec, b: Vec, t: number): Vec => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
export const mid = (a: Vec, b: Vec): Vec => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
export const perp = (a: Vec): Vec => ({ x: -a.y, y: a.x });
export const equals = (a: Vec, b: Vec, eps = EPS): boolean => Math.abs(a.x - b.x) <= eps && Math.abs(a.y - b.y) <= eps;

export function normalize(a: Vec): Vec {
  const l = len(a);
  return l < EPS ? { x: 0, y: 0 } : { x: a.x / l, y: a.y / l };
}

/** Rotates a vector around the origin by `rad` radians (positive = clockwise on a y-down screen). */
export function rotate(a: Vec, rad: number): Vec {
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  return { x: a.x * c - a.y * s, y: a.x * s + a.y * c };
}

export function rotateAround(a: Vec, center: Vec, rad: number): Vec {
  return add(rotate(sub(a, center), rad), center);
}

export const degToRad = (d: number): number => (d * Math.PI) / 180;
export const radToDeg = (r: number): number => (r * 180) / Math.PI;

/** Distance from point p to the segment a-b. */
export function distToSegment(p: Vec, a: Vec, b: Vec): number {
  return dist(p, closestOnSegment(p, a, b));
}

export function closestOnSegment(p: Vec, a: Vec, b: Vec): Vec {
  const ab = sub(b, a);
  const l2 = dot(ab, ab);
  if (l2 < EPS * EPS) return a;
  const t = Math.max(0, Math.min(1, dot(sub(p, a), ab) / l2));
  return { x: a.x + ab.x * t, y: a.y + ab.y * t };
}

/** Parameter t (0..1) of the projection of p onto segment a-b. */
export function projectT(p: Vec, a: Vec, b: Vec): number {
  const ab = sub(b, a);
  const l2 = dot(ab, ab);
  if (l2 < EPS * EPS) return 0;
  return Math.max(0, Math.min(1, dot(sub(p, a), ab) / l2));
}

/**
 * Proper intersection test between segments p1-p2 and q1-q2.
 * Returns the intersection parameter on the first segment, or null.
 * Collinear overlaps are reported as non-intersecting (callers treat them separately).
 */
export function segmentIntersection(p1: Vec, p2: Vec, q1: Vec, q2: Vec): { t: number; u: number; point: Vec } | null {
  const r = sub(p2, p1);
  const s = sub(q2, q1);
  const denom = cross(r, s);
  if (Math.abs(denom) < 1e-12) return null;
  const qp = sub(q1, p1);
  const t = cross(qp, s) / denom;
  const u = cross(qp, r) / denom;
  if (t < 0 || t > 1 || u < 0 || u > 1) return null;
  return { t, u, point: { x: p1.x + r.x * t, y: p1.y + r.y * t } };
}

/** Rounds to a fixed number of decimals; used to keep serialized geometry compact and stable. */
export function roundTo(v: number, decimals = 4): number {
  const f = 10 ** decimals;
  return Math.round(v * f) / f;
}
